/**
 * Persists the donor's own chosen payment gateway for the length of the checkout session
 * (WP-44: "Selection persists within the checkout session").
 *
 * ## `sessionStorage`, not `localStorage`
 *
 * `draft-storage.js`'s own `localStorage` choice is deliberate for the draft — a donor who
 * closes the tab and comes back tomorrow should see their amount and name again. A gateway
 * choice is different: it is a property of *this* attempt at giving, not something that should
 * survive to a visit next week and quietly pre-select a payment method the donor never chose on
 * that later occasion. `sessionStorage` is cleared when the tab closes, which is exactly the
 * "checkout session" WP-44's own requirement means.
 *
 * Every read and write is wrapped, the identical discipline `draft-storage.js`'s own docblock
 * states: a private window, a blocked storage API, or a full quota degrades to "no selection
 * remembered," never to a thrown error that stops the checkout from rendering.
 */

const PREFIX = 'anudan_checkout_gateway_';

/**
 * @param {number} fundId
 * @return {string}
 */
function key( fundId ) {
	return PREFIX + String( fundId );
}

/**
 * Remembers the donor's chosen gateway for this fund, for the rest of this browser tab's
 * session. Silently does nothing if storage is unavailable.
 *
 * @param {number} fundId
 * @param {string} gatewayId
 * @return {void}
 */
export function saveGatewaySelection( fundId, gatewayId ) {
	try {
		window.sessionStorage.setItem( key( fundId ), gatewayId );
	} catch ( error ) {
		// Storage unavailable. The donor's choice still works for this render; it just is not
		// remembered if the form is rebuilt.
	}
}

/**
 * Returns the donor's remembered gateway choice for this fund, or null when there is none or
 * storage is unavailable.
 *
 * @param {number} fundId
 * @return {string|null}
 */
export function loadGatewaySelection( fundId ) {
	try {
		return window.sessionStorage.getItem( key( fundId ) );
	} catch ( error ) {
		return null;
	}
}
